import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Cpu, Sparkles, Image, MessageSquare } from 'lucide-react';
import StaticPageLayout from '../../../components/StaticPageLayout';
import { useThemeStore } from '../../../store/theme';

const tableOfContents = [
  { id: 'ai-models', title: 'What are the AI Models used in SweetPilot App right now?' },
];

const relatedArticles = [
  { title: 'What is SweetPilot?', href: '/doc/article/what-is-sweetpilot' },
  { title: 'SweetPilot AI Employees', href: '/employees' },
  { title: 'How to get started with SweetPilot', href: '/doc/article/getting-started' },
  { title: 'Understanding AI Hallucinations', href: '/doc/article/ai-hallucinations' },
];

const aiModels = [
  {
    name: 'GPT-4o',
    provider: 'OpenAI',
    description: 'Advanced language model for complex reasoning and content generation',
    icon: '🧠',
  },
  {
    name: 'Claude 3.5 Sonnet',
    provider: 'Anthropic',
    description: 'Excellent for nuanced conversations and detailed analysis',
    icon: '💬',
  },
  {
    name: 'Gemini 2.0 Flash',
    provider: 'Google',
    description: 'Fast and efficient for quick responses and tasks',
    icon: '⚡',
  },
  {
    name: 'DALL-E 3',
    provider: 'OpenAI',
    description: 'High-quality image generation from text descriptions',
    icon: '🎨',
  },
];

export default function AIModels() {
  const { theme } = useThemeStore();
  const isDark = theme === 'dark';
  const [activeSection, setActiveSection] = useState('');
  const [isAtTop, setIsAtTop] = useState(true);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  // SEO Schema markup
  const schemaMarkup = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Which AI Models Does SweetPilot Use? - GPT-4, Claude, Gemini & DALL-E',
    description: 'Learn about the AI models powering SweetPilot: GPT-4o, Claude 3.5 Sonnet, Gemini 2.0 Flash, and DALL-E 3. Discover image generation, custom automation, and more.',
    author: { '@type': 'Organization', name: 'SweetPilot' },
    publisher: { '@type': 'Organization', name: 'SweetPilot', logo: { '@type': 'ImageObject', url: 'https://sweetpilot.com/logo.png' } },
    datePublished: '2024-01-01',
    dateModified: new Date().toISOString().split('T')[0],
    mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://sweetpilot.com/doc/article/ai-models' }
  };

  useEffect(() => {
    const handleScroll = () => {
      const sections = tableOfContents.map(item => document.getElementById(item.id));
      const scrollPosition = window.scrollY + 100;

      const firstSection = sections[0];
      if (firstSection && scrollPosition < firstSection.offsetTop) {
        setIsAtTop(true);
        setActiveSection('');
        return;
      }

      setIsAtTop(false);

      for (let i = sections.length - 1; i >= 0; i--) {
        const section = sections[i];
        if (section && section.offsetTop <= scrollPosition) {
          setActiveSection(tableOfContents[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  const faqItems = [
    {
      question: 'Can I create a custom automation in SweetPilot?',
      answer: 'Yes! SweetPilot offers automation capabilities that allow you to set up workflows that run in the background. You can configure triggers, schedules, and actions to automate repetitive tasks. Visit the Automations section in your dashboard to explore available options.',
    },
    {
      question: 'How are these AI Employees better than ChatGPT?',
      answer: 'While ChatGPT is a general-purpose AI assistant, SweetPilot AI Employees are specialized for specific business tasks. Each employee has unique expertise, power-ups, and is connected to your Brain AI knowledge base for personalized responses. They also integrate with your existing tools and can run automations - something ChatGPT alone cannot do.',
    },
  ];

  return (
    <>
      <Helmet>
        <title>Which AI Models Does SweetPilot Use? - GPT-4, Claude, Gemini & DALL-E | SweetPilot Help Center</title>
        <meta name="description" content="Discover the AI models powering SweetPilot: GPT-4o for complex reasoning, Claude 3.5 Sonnet for analysis, Gemini 2.0 Flash for speed, and DALL-E 3 for image generation." />
        <meta name="keywords" content="SweetPilot AI models, GPT-4o, Claude 3.5, Gemini 2.0, DALL-E 3, AI image generation, OpenAI, Anthropic, Google AI, AI automation" />
        <meta property="og:title" content="SweetPilot AI Models - GPT-4, Claude, Gemini, DALL-E" />
        <meta property="og:description" content="Learn about the cutting-edge AI models powering SweetPilot employees." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://sweetpilot.com/doc/article/ai-models" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="SweetPilot AI Models" />
        <meta name="twitter:description" content="GPT-4o, Claude 3.5, Gemini 2.0, and DALL-E 3 power SweetPilot." />
        <link rel="canonical" href="https://sweetpilot.com/doc/article/ai-models" />
        <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
      </Helmet>

      <StaticPageLayout
        title=""
        subtitle=""
        showBackButton={false}
      >
        <div className="flex gap-16 lg:gap-20">
        {/* Main Content */}
        <div className="flex-1 max-w-2xl">
          {/* Breadcrumbs */}
          <nav className="mb-8">
            <ol className={`flex items-center gap-2 text-sm flex-wrap ${isDark ? 'text-gray-400' : 'text-slate-500'}`}>
              <li>
                <Link
                  to="/docs"
                  className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                >
                  All Collections
                </Link>
              </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li>
                <Link
                  to="/doc/collections/getting-started"
                  className={`transition-colors ${isDark ? 'hover:text-purple-400' : 'hover:text-purple-600'}`}
                >
                  Getting started
                </Link>
              </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li>
                <span className={isDark ? 'text-gray-500' : 'text-slate-400'}>General info</span>
              </li>
              <li>
                <ChevronRight className="w-4 h-4" />
              </li>
              <li className={isDark ? 'text-white' : 'text-slate-900'}>
                Which AI Models does SweetPilot use?
              </li>
            </ol>
          </nav>

          {/* Article Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-10"
          >
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
                isDark ? 'bg-purple-500/20' : 'bg-purple-100'
              }`}>
                <Cpu className={`w-6 h-6 ${isDark ? 'text-purple-400' : 'text-purple-600'}`} />
              </div>
              <h1 className={`text-4xl font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Which AI Models does SweetPilot use?
              </h1>
            </div>
            <p className={`text-lg ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
              Learn about the AI models behind SweetPilot AI Employees and find answers to common questions along with some example prompts.
            </p>
          </motion.div>

          {/* Article Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className={`prose max-w-none ${isDark ? 'prose-invert' : ''}`}
          >
            {/* What are the AI Models */}
            <h2 id="ai-models" className={`text-2xl font-bold mt-12 mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              What are the AI Models used in SweetPilot App right now?
            </h2>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot AI Employees are powered by a combination of state-of-the-art AI models to deliver the best results for different tasks:
            </p>

            {/* AI Models Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
              {aiModels.map((model, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-xl border ${
                    isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
                  }`}
                >
                  <div className="flex items-center gap-3 mb-2">
                    <span className="text-2xl">{model.icon}</span>
                    <div>
                      <h3 className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}>
                        {model.name}
                      </h3>
                      <p className={`text-xs ${isDark ? 'text-purple-400' : 'text-purple-600'}`}>
                        {model.provider}
                      </p>
                    </div>
                  </div>
                  <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                    {model.description}
                  </p>
                </div>
              ))}
            </div>

            <p className={`mb-8 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              We continuously evaluate and integrate the latest AI models to ensure SweetPilot provides the best possible experience. The specific model used depends on the task and the AI employee you're working with.
            </p>

            {/* Can I generate multiple images */}
            <h3 className={`text-xl font-semibold mt-10 mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Can I generate multiple images in one theme?
            </h3>
            <p className={`mb-6 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Yes. You can generate multiple images with one request, using the same reference points described in your message or displayed in attached files. This is perfect for creating consistent branding materials, social media content packs, or variations of a design concept.
            </p>

            {/* Image dimensions */}
            <h3 className={`text-xl font-semibold mt-10 mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              What are the dimensions SweetPilot generates images in?
            </h3>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              SweetPilot AI Employees can generate images in multiple formats:
            </p>
            <ul className={`list-disc pl-6 mb-6 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li><strong>1:1</strong> - Square format, perfect for profile pictures and Instagram posts</li>
              <li><strong>4:5</strong> - Portrait format, ideal for Instagram feed posts</li>
              <li><strong>16:9</strong> - Landscape format, great for YouTube thumbnails and presentations</li>
              <li><strong>9:16</strong> - Vertical format, perfect for Stories and Reels</li>
            </ul>

            {/* Can AI edit images */}
            <h3 className={`text-xl font-semibold mt-10 mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Can SweetPilot AI Employees edit images?
            </h3>
            <p className={`mb-4 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              Yes, your AI employees can edit an uploaded or a previously generated image in various ways. They can remove, add, and change elements (e.g. object's color or shape).
            </p>
            <div className={`p-4 rounded-xl border mb-6 ${
              isDark ? 'bg-amber-500/10 border-amber-500/20' : 'bg-amber-50 border-amber-100'
            }`}>
              <p className={`text-sm font-medium ${isDark ? 'text-amber-200' : 'text-amber-800'}`}>
                <strong>Important:</strong> Make sure you're very specific about the details you want preserved and be very clear about what you'd like to see in the new image.
              </p>
            </div>

            <div className={`p-4 rounded-xl font-mono text-sm mb-6 ${
              isDark ? 'bg-dark-800/80 text-gray-300' : 'bg-slate-100 text-slate-700'
            }`}>
              <p className="mb-2 text-xs uppercase tracking-wide opacity-60">Example prompt:</p>
              <p>Using the provided image of [subject], please [action: add/remove/modify] [element] to/from the scene. Ensure the change is [description of how the change should integrate].</p>
            </div>

            {/* How to add logo */}
            <h3 className={`text-xl font-semibold mt-10 mb-3 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              How can I add my logo to an image?
            </h3>
            <ol className={`list-decimal pl-6 mb-4 space-y-2 ${isDark ? 'text-gray-300' : 'text-slate-700'}`}>
              <li>Attach the logo image (and other images if you want them combined) to your message.</li>
              <li>Make your instructions as clear as possible:</li>
            </ol>

            <div className={`p-4 rounded-xl font-mono text-sm mb-6 ${
              isDark ? 'bg-dark-800/80 text-gray-300' : 'bg-slate-100 text-slate-700'
            }`}>
              <p className="mb-2 text-xs uppercase tracking-wide opacity-60">Example prompt:</p>
              <p>Using the provided images, place [element from logo image] onto [element from image 2]. Ensure that the features of [element from image 2] remain completely unchanged. The added element should [description of how the element should integrate].</p>
            </div>

            {/* Chat Example Image */}
            <div className={`my-8 rounded-xl overflow-hidden border ${isDark ? 'border-white/10' : 'border-slate-200'}`}>
              <img
                src="/docs/ai-models/chat-example.png"
                alt="Example conversation showing image generation with logo placement"
                className="w-full"
              />
            </div>

            {/* Expandable FAQ */}
            <div className="space-y-3 my-10">
              {faqItems.map((item, index) => (
                <div
                  key={index}
                  className={`rounded-xl border overflow-hidden ${
                    isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
                  }`}
                >
                  <button
                    onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                    className={`w-full flex items-center gap-3 p-4 text-left font-medium ${
                      isDark ? 'text-white hover:bg-white/5' : 'text-slate-900 hover:bg-slate-50'
                    }`}
                  >
                    <span className={`transition-transform ${expandedFaq === index ? 'rotate-90' : ''}`}>▶</span>
                    {item.question}
                  </button>
                  {expandedFaq === index && (
                    <div className={`px-4 pb-4 pl-10 ${isDark ? 'text-gray-400' : 'text-slate-600'}`}>
                      {item.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Divider */}
            <hr className={`my-12 ${isDark ? 'border-white/10' : 'border-slate-200'}`} />

            {/* Related Articles */}
            <h2 className={`text-2xl font-bold mb-6 ${isDark ? 'text-white' : 'text-slate-900'}`}>
              Related Articles
            </h2>
            <div className={`rounded-2xl border overflow-hidden ${
              isDark ? 'bg-dark-800/50 border-white/10' : 'bg-white border-slate-200'
            }`}>
              {relatedArticles.map((article, index) => (
                <Link
                  key={index}
                  to={article.href}
                  className={`flex items-center justify-between p-4 transition-colors ${
                    index !== relatedArticles.length - 1 ? (isDark ? 'border-b border-white/5' : 'border-b border-slate-100') : ''
                  } ${isDark ? 'hover:bg-white/5' : 'hover:bg-slate-50'}`}
                >
                  <span className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}>
                    {article.title}
                  </span>
                  <ChevronRight className={`w-5 h-5 ${isDark ? 'text-gray-500' : 'text-slate-400'}`} />
                </Link>
              ))}
            </div>

            {/* Feedback Section */}
            <div className={`mt-12 p-8 rounded-2xl text-center ${
              isDark ? 'bg-dark-800/50 border border-white/10' : 'bg-slate-50 border border-slate-200'
            }`}>
              <p className={`text-lg font-medium mb-4 ${isDark ? 'text-white' : 'text-slate-900'}`}>
                Did this answer your question?
              </p>
              <div className="flex justify-center gap-4">
                <button className="text-3xl hover:scale-110 transition-transform" title="No">😞</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Somewhat">😐</button>
                <button className="text-3xl hover:scale-110 transition-transform" title="Yes">😃</button>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Right Sidebar - Table of Contents */}
        <aside className="hidden lg:block w-56 shrink-0 ml-auto">
          <div className="sticky top-24 pl-6 border-l border-slate-200 dark:border-white/10">
            {/* Sidebar Title */}
            <h3 className={`text-base mb-4 ${
              isAtTop
                ? isDark ? 'text-white font-semibold' : 'text-slate-900 font-semibold'
                : isDark ? 'text-gray-400' : 'text-slate-600'
            }`}>
              What are the AI Models used in SweetPilot App right now?
            </h3>
            <nav className="space-y-2">
              {tableOfContents.map((item, index) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left py-1.5 text-sm transition-colors ${
                    activeSection === item.id
                      ? isDark
                        ? 'text-white font-medium'
                        : 'text-slate-900 font-medium'
                      : isAtTop && index === 0
                        ? isDark
                          ? 'text-white font-medium'
                          : 'text-slate-900 font-medium'
                        : isDark
                          ? 'text-gray-500 hover:text-gray-300'
                          : 'text-slate-500 hover:text-slate-700'
                  }`}
                >
                  {item.title}
                </button>
              ))}
            </nav>
          </div>
        </aside>
      </div>

      {/* Footer */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.3 }}
        className="text-center mt-16"
      >
        <div className={`w-12 h-12 rounded-full mx-auto mb-3 flex items-center justify-center ${
          isDark ? 'bg-purple-500/20' : 'bg-purple-100'
        }`}>
          <span className="text-2xl">🤖</span>
        </div>
        <a
          href="mailto:help@sweetpilot.com"
          className={`text-sm transition-colors ${
            isDark ? 'text-purple-400 hover:text-purple-300' : 'text-purple-600 hover:text-purple-700'
          }`}
        >
          help@sweetpilot.com
        </a>
      </motion.div>
      </StaticPageLayout>
    </>
  );
}
